import React, { useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';

const screenshots = [
  {
    id: 1,
    title: 'Executive Overview Dashboard',
    description: 'Real-time visibility across all operations',
    image: require('../../assets/Executive Dashboard.png'),
    placeholder: 'Executive Dashboard'
  },
  {
    id: 2,
    title: 'Trade Execution View',
    description: 'Complete trade workflow management',
    image: require('../../assets/Trade Execution.png'),
    placeholder: 'Trade Execution'
  },
  {
    id: 3,
    title: 'Risk & Alerts',
    description: 'Anomaly detection and risk signals',
    image: require('../../assets/Risk and alerts.png'),
    placeholder: 'Risk Management'
  },
  {
    id: 4,
    title: 'Analytics & Trends',
    description: 'Performance analysis and forecasting',
    image: require('../../assets/Trends.png'),
    placeholder: 'Analytics'
  }
];

const ProductScreenshots = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  return (
    <section className="relative py-24 lg:py-32 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            See how intelligence flows across your enterprise
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Copy */}
          <div className="space-y-8">
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              TradeEngine.AI connects trade execution, operations, finance, and sustainability 
              into a single intelligence layer — without replacing your existing systems.
            </p>

            <ul className="space-y-4">
              {[
                'Unified operational intelligence',
                'Cross-functional visibility',
                'Real-time risk and performance signals',
                'Enterprise-grade security and scalability'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">{item}</span>
                </li>
              ))}
            </ul>

            {/* Screenshot Navigation - Desktop */}
            <div className="hidden lg:block pt-8">
              <div className="space-y-3">
                {screenshots.map((screenshot, index) => (
                  <button
                    key={screenshot.id}
                    onClick={() => setActiveIndex(index)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                      index === activeIndex
                        ? 'bg-slate-100 dark:bg-slate-800 border-l-2 border-orange-500'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <div className={`font-medium ${index === activeIndex ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                      {screenshot.title}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-500">
                      {screenshot.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Screenshots */}
          <div className="relative">
            {/* Screenshot Display */}
            <div className="relative aspect-[4/3] bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg">
              {/* Actual Screenshot Image */}
              <img
                src={screenshots[activeIndex].image}
                alt={screenshots[activeIndex].title}
                className="w-full h-full object-cover"
              />
              
              {/* Screenshot Label */}
              <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300">
                {screenshots[activeIndex].placeholder}
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="flex items-center justify-between mt-4 lg:hidden">
              <button
                onClick={prevSlide}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </button>
              
              <div className="flex gap-2">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === activeIndex
                        ? 'bg-orange-500 w-6'
                        : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextSlide}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductScreenshots;
